import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Play, Trash2, Archive, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

interface ColumnMapping {
  // Persisted in JSONB by Jackson default naming (camelCase) — Spring
  // serialises the Kotlin ColumnMapping data class as-is. Earlier
  // versions of this page expected snake_case (matching the LLM
  // prompt's wire format), which left every Column / Entity / Field
  // cell rendering as "—". The runtime shape is camelCase.
  sourceColumn: string;
  targetEntity: string | null;
  targetField: string | null;
  confidence: number;
  suggestion?: string | null;
}

interface ImportSessionRow {
  id: string;
  sourceRowIndex: number;
  rawRow: Record<string, unknown>;
  action: string | null;
  targetEntityType: string | null;
  targetEntityId: string | null;
  confidence: number | null;
  llmReasoning: string | null;
  extracted: { records?: Array<{ entity: string; fields: Record<string, unknown>; confidence: number }> } | null;
}

interface ImportSessionDetail {
  session: {
    id: string;
    campaignId: string | null;
    filename: string;
    version: number;
    parentSessionId: string | null;
    status: string;
    rowCount: number | null;
    importedCount: number | null;
    updatedCount: number | null;
    failedCount: number | null;
    llmProvider: string | null;
    llmModel: string | null;
    errorMessage: string | null;
    createdAt: string | null;
    summary: { mappings?: ColumnMapping[]; primary_entity?: string; columns?: string[] } | null;
  };
  sample: {
    columns: string[];
    preview: Array<Record<string, string | null>>;
    skippedSheets: string[];
  };
  rows: ImportSessionRow[];
}

const STATUS_BADGE: Record<string, string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  MAPPING: 'bg-amber-900/40 text-amber-200',
  EXTRACTING: 'bg-amber-900/40 text-amber-200',
  APPLYING: 'bg-amber-900/40 text-amber-200',
  COMPLETED: 'bg-blue-900/40 text-blue-200',
  APPLIED: 'bg-emerald-900/40 text-emerald-200',
  FAILED: 'bg-red-900/40 text-red-200',
};

const POLL_INTERVAL_MS = 3000;

export function ImportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState<ImportSessionDetail | null>(null);
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [actionPending, setActionPending] = useState<'discard' | 'archive' | null>(null);
  const pollTimer = useRef<number | null>(null);

  function fetchOnce() {
    if (!id) return;
    apiClient
      .get<ImportSessionDetail>(`/imports/${id}`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }

  useEffect(() => {
    fetchOnce();
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Poll while running
  useEffect(() => {
    if (!data) return;
    const isRunning = ['DRAFT', 'MAPPING', 'EXTRACTING', 'APPLYING'].includes(data.session.status);
    if (isRunning && !pollTimer.current) {
      pollTimer.current = window.setInterval(fetchOnce, POLL_INTERVAL_MS);
    } else if (!isRunning && pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
      pollTimer.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.session.status]);

  async function runPipeline() {
    if (!id) return;
    setRunning(true);
    try {
      await apiClient.post(`/imports/${id}/run`);
      // Poller will pick up the status transitions.
      setTimeout(fetchOnce, 500);
    } finally {
      setRunning(false);
    }
  }

  async function applyImport() {
    if (!id) return;
    if (!confirm(t('imports.detail_apply_confirm'))) return;
    setApplying(true);
    try {
      await apiClient.post(`/imports/${id}/apply`);
      setTimeout(fetchOnce, 500);
    } finally {
      setApplying(false);
    }
  }

  async function discardSession() {
    if (!id) return;
    if (!confirm(t('imports.detail_discard_confirm'))) return;
    setActionPending('discard');
    try {
      await apiClient.post(`/imports/${id}/discard`);
      navigate('/admin/imports');
    } catch {
      setActionPending(null);
    }
  }

  async function archiveSession() {
    if (!id) return;
    setActionPending('archive');
    try {
      await apiClient.delete(`/imports/${id}`);
      navigate('/admin/imports');
    } catch {
      setActionPending(null);
    }
  }

  if (!data) return <p className="p-6 text-sm text-muted-foreground">{t('common.loading')}</p>;

  const { session, sample, rows } = data;
  const inProgress = ['DRAFT', 'MAPPING', 'EXTRACTING', 'APPLYING'].includes(session.status);

  return (
    <div className="space-y-6 p-6">
      <Breadcrumbs
        crumbs={[
          { label: t('imports.title'), to: '/admin/imports' },
          { label: session.filename },
        ]}
      />

      <Link to="/admin/imports" className="inline-flex items-center gap-1 text-sm text-primary">
        <ArrowLeft className="h-4 w-4" /> {t('imports.title')}
      </Link>

      <Card>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold">{session.filename}</h1>
              {session.version > 1 && (
                <span className="text-sm text-muted-foreground">v{session.version}</span>
              )}
              <span
                className={`rounded-full px-3 py-0.5 text-xs font-medium ${
                  STATUS_BADGE[session.status] ?? 'bg-muted'
                }`}
              >
                {t(`imports.detail_status_${session.status.toLowerCase()}`, session.status)}
              </span>
            </div>
            {session.llmProvider && (
              <p className="mt-1 text-xs text-muted-foreground">
                {session.llmProvider}{session.llmModel ? ` / ${session.llmModel}` : ''}
              </p>
            )}
            {session.errorMessage && (
              <p className="mt-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {session.errorMessage}
              </p>
            )}
          </div>
          {(session.status === 'DRAFT' || session.status === 'FAILED') && (
            <Button onClick={runPipeline} disabled={running}>
              <Play className="mr-1 h-4 w-4" /> {t('imports.detail_run')}
            </Button>
          )}
          {session.status === 'COMPLETED' && (
            <Button onClick={applyImport} disabled={applying}>
              <CheckCircle2 className="mr-1 h-4 w-4" />
              {applying ? t('imports.detail_applying') : t('imports.detail_apply')}
            </Button>
          )}
        </div>
      </Card>

      {session.status === 'COMPLETED' && (
        <Card className="border-blue-800 bg-blue-950/40">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-blue-300" />
            <div className="flex-1 text-sm">
              <h3 className="font-semibold">{t('imports.detail_ready_title')}</h3>
              <p className="mt-1 text-muted-foreground">
                {t('imports.detail_ready_help')}
              </p>
            </div>
          </div>
        </Card>
      )}

      {session.status === 'APPLIED' && (
        <Card className="border-emerald-800 bg-emerald-950/40">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" />
            <div className="flex-1 text-sm">
              <h3 className="font-semibold">{t('imports.detail_applied_title')}</h3>
              <p className="mt-1 text-muted-foreground">
                {t('imports.detail_applied_help', {
                  imported: session.importedCount ?? '—',
                  deduped: session.updatedCount ?? 0,
                  failed: session.failedCount ?? 0,
                })}
              </p>
            </div>
          </div>
        </Card>
      )}

      {session.status === 'FAILED' && (
        <Card className="border-destructive/40 bg-destructive/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            <div className="flex-1 space-y-3">
              <div>
                <h3 className="text-sm font-semibold">
                  {t('imports.detail_failed_title')}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('imports.detail_failed_help')}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={discardSession}
                  disabled={actionPending !== null}
                >
                  <Trash2 className="mr-1 h-4 w-4" />
                  {actionPending === 'discard'
                    ? t('imports.detail_discarding')
                    : t('imports.detail_discard')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={archiveSession}
                  disabled={actionPending !== null}
                >
                  <Archive className="mr-1 h-4 w-4" />
                  {actionPending === 'archive'
                    ? t('imports.detail_archiving')
                    : t('imports.detail_keep_archived')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={runPipeline}
                  disabled={running || actionPending !== null}
                >
                  <Play className="mr-1 h-4 w-4" />
                  {t('imports.detail_run_again')}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {sample.columns.length > 0 && (
        <Card>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('imports.detail_columns')} ({sample.columns.length})
          </h2>
          <div className="flex flex-wrap gap-2">
            {sample.columns.map((c) => (
              <span key={c} className="rounded-md bg-muted px-2 py-1 text-xs font-mono">
                {c}
              </span>
            ))}
          </div>
          {sample.skippedSheets.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              {t('imports.detail_skipped')}: {sample.skippedSheets.join(', ')}
            </p>
          )}
        </Card>
      )}

      <SchemaMappingCard summary={session.summary} />

      {rows.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="border-b bg-muted/40 px-4 py-3">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('imports.detail_rows_table')} ({rows.length})
            </h2>
          </div>
          <div className="max-h-[28rem] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Action</th>
                  <th className="px-3 py-2 text-right">Conf.</th>
                  <th className="px-3 py-2 text-left">Extracted</th>
                  <th className="px-3 py-2 text-left">Raw row</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t align-top">
                    <td className="px-3 py-2 font-mono text-xs">{r.sourceRowIndex}</td>
                    <td className="px-3 py-2">
                      {r.action ? (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                          {t(`imports.row_action_${r.action}`, r.action)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {r.confidence != null ? `${(r.confidence * 100).toFixed(0)}%` : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.extracted?.records ? (
                        <ul className="space-y-1">
                          {r.extracted.records.map((rec, i) => (
                            <li key={i}>
                              <span className="font-semibold">{rec.entity}</span>:{' '}
                              <code className="rounded bg-muted px-1 text-[10px]">
                                {Object.entries(rec.fields)
                                  .filter(([, v]) => v != null && v !== '')
                                  .map(([k, v]) => `${k}=${String(v)}`)
                                  .join(', ')}
                              </code>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      <code className="block max-w-md truncate">
                        {Object.entries(r.rawRow)
                          .map(([k, v]) => `${k}=${String(v ?? '')}`)
                          .join(' · ')}
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {inProgress && (
        <p className="text-center text-xs text-muted-foreground">
          {t(`imports.detail_status_${session.status.toLowerCase()}`, session.status)}
        </p>
      )}
    </div>
  );
}

function SchemaMappingCard({ summary }: { summary: unknown }) {
  const { t } = useTranslation();
  // The session.summary JSONB shape is { columns, mappings, primary_entity, ... }
  // depending on pipeline phase. We render mappings if present.
  if (!summary || typeof summary !== 'object') return null;
  const s = summary as { mappings?: ColumnMapping[]; primary_entity?: string };
  if (!s.mappings || s.mappings.length === 0) return null;
  return (
    <Card className="overflow-hidden p-0">
      <div className="border-b bg-muted/40 px-4 py-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('imports.detail_mappings')}
          {s.primary_entity && (
            <span className="ml-2 text-[10px] text-muted-foreground">
              · primary: {s.primary_entity}
            </span>
          )}
        </h2>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-2 text-left">Column</th>
            <th className="px-4 py-2 text-left">Entity</th>
            <th className="px-4 py-2 text-left">Field</th>
            <th className="px-4 py-2 text-right">Confidence</th>
            <th className="px-4 py-2 text-left">Suggestion</th>
          </tr>
        </thead>
        <tbody>
          {s.mappings.map((m, i) => (
            <tr key={i} className="border-t">
              <td className="px-4 py-2 font-mono text-xs">{m.sourceColumn}</td>
              <td className="px-4 py-2">{m.targetEntity ?? '—'}</td>
              <td className="px-4 py-2 font-mono text-xs">{m.targetField ?? '—'}</td>
              <td className="px-4 py-2 text-right font-mono text-xs">
                {(m.confidence * 100).toFixed(0)}%
              </td>
              <td className="px-4 py-2 text-xs text-muted-foreground">{m.suggestion ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
